'use client';

import React, { ComponentProps, ElementType, useEffect } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import errorCallback from '../../../../lib/errorCallback';
import { NoflakeTag, Product } from '../../types';
import DocumentBody from '../features/DocumentBody';
import KitDynamicLayout, {
  LayoutTree,
  DEVICE_LAYOUT_NAME,
} from '../features/DynamicLayout';
import PackEddPanel from '../features/EddPanel/EddPanel';
import NotificationCenter, { Channels } from '../features/NotificationCenter';
import DynamicHeader from '../features/DynamicHeader';
import Feedback from '../features/Feedback/Feedback';
import BlockAsset from '../features/AssetBlock';
import HelpPill from '../features/HelpPill/HelpPill';
import SectionDivider from '../features/SectionDivider/SectionDivider';
import BroadcastBanner from '../features/BroadcastBanner/BroadcastBanner';
import GlobalStyleTag from '../features/GlobalStyleTag';
import CpCustomComponent from '../features/CpCustomComponent/CpCustomComponent';
import CustomComponent from '../features/CustomComponent/CustomComponent';
import ItemsInThisShipment from '../features/ItemsInThisShipment';
import MoreInformation from '../features/MoreInformation/MoreInformation';
import PickupLocation from '../features/PickupLocation';
import Shipments from '../features/Shipments';
import Empty from '../features/Empty';
import NarvarFooter from '../features/NarvarFooter/NarvarFooter';
import ErrorBoundary from '../features/ErrorBoundary';
import DeliveryStatus from '../features/DeliveryStatus';
import DriverDetails from '../features/DriverDetails';
import LocalDeliveryMap from '../features/LocalDeliveryMap/LocalDeliveryMap';
import GiftTracking from '../features/GiftTracking/GiftTracking';
import Chat, { ChatProvider } from '../features/ChatAgent/KitChat';
import {
  PageProvider,
  usePageContext,
  useTitanAnalytics,
} from '../../hooks/PageContext/usePageContext';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../hooks/Analytics/useAnalyticsPost';
import Features from './features';
// eslint-disable-next-line import/no-named-as-default
import TrackPageTitle from './TrackPageTitle';

interface DynamicTrackPageProps {
  renderHeader?: boolean | null;
  customComponents?: Record<
    string,
    ElementType | undefined | null | string
  > | null;
  userAgentDevice?: LayoutTree.Devices;
  growthbookDisabled?: boolean;
  growthbookKey?: string;
  growthbookDecryptionKey?: string;
  growthbookDevModeEnabled?: boolean;
}

const DynamicTrackPage = ({
  renderHeader = null,
  customComponents = null,
  userAgentDevice,
}: DynamicTrackPageProps) => {
  const titan = useTitanAnalytics();
  const logEvent = useAnalyticsPost();

  // need to call this to trigger `trackingCallback` and send attrs to gb
  // before chat component renders if we want to run any experiments
  useFeatureIsOn('help_pill-chat');

  useEffect(() => {
    titan
      ?.page({
        type: 'page',
        name: 'Dynamic Track',
        channel: 'web',
        context: {},
        properties: {
          url: window.location.href,
          title: document.title,
          narvar_product_context: 'track',
        },
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e));
  }, [titan]);

  useEffect(() => {
    logEvent({
      eventType: NarvarEventTypes.PAGE_LOAD,
      eventComponent: NarvarEventComponents.NTH_KIT_DYNAMIC_LAYOUT_NEXT,
      eventFeature: NarvarEventComponents.NTH_KIT_DYNAMIC_LAYOUT_NEXT,
      pageComponent: NarvarEventComponents.NTH_KIT_DYNAMIC_LAYOUT_NEXT,
      trackLayout: DEVICE_LAYOUT_NAME,
    });
  }, []);

  const { state } = usePageContext();

  return (
    <div style={{ whiteSpace: 'pre-line' }}>
      <GlobalStyleTag />
      <DocumentBody />
      <TrackPageTitle />
      {/* FIXME: temporarily moving up this provider so the help pill can
      access chat state. remove once we find a better solution for managing
      both on the page at once.
      */}
      <ChatProvider>
        {/* FIXME: we probably want chat to be defined in track layout settings,
        but for beta/testing this is the easiest way. display logic is in 
        nth-kit-chat itself and is not settings/retailer dependent for now.
        */}
        <Chat />
        <KitDynamicLayout
          userAgentDevice={userAgentDevice}
          feature={(options) => {
            if (
              !options ||
              options.node.name !== Features.DYNAMIC_HEADER ||
              !renderHeader
            ) {
              return <></>;
            }

            return (
              <ErrorBoundary errorContent={null}>
                <DynamicHeader
                  featureName="narvarHeader"
                  key={Features.DYNAMIC_HEADER + options.node.id}
                  templateRenderingContext={state}
                  skipToContent={{
                    targetId: 'content',
                    scrollToTarget: false,
                  }}
                />
              </ErrorBoundary>
            );
          }}
        />
        <main role="main">
          <KitDynamicLayout
            userAgentDevice={userAgentDevice}
            feature={(options) => {
              if (!options) {
                return <></>;
              }

              // NOTE: `name` is of type `string`, but we want to treat it as a
              // feature and do an exhaustive match over it, but still handle when the
              // `string` does not match a feature enum.
              const feature = options.node.name as Features;

              const renderFeature = () => {
                switch (feature) {
                  case Features.DYNAMIC_HEADER: {
                    return <></>;
                  }
                  case Features.EMPTY: {
                    return <Empty key={Features.EMPTY + options.node.id} />;
                  }
                  case Features.NARVAR_FOOTER: {
                    return <></>;
                  }
                  case Features.BROADCAST_BANNER: {
                    return (
                      <BroadcastBanner
                        key={Features.BROADCAST_BANNER + options.node.id}
                        featureName="broadcastBanner"
                      />
                    );
                  }
                  case Features.GIFT_SHARING: {
                    return (
                      <GiftTracking
                        key={Features.GIFT_SHARING + options.node.id}
                        featureName="giftTracking"
                        userAgentDevice={userAgentDevice}
                      />
                    );
                  }
                  case Features.EDD_PANEL: {
                    return (
                      <PackEddPanel
                        key={Features.EDD_PANEL + options.node.id}
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        featureName="eddPanel"
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.SMS_NOTIFICATIONS: {
                    return (
                      <NotificationCenter
                        key={Features.SMS_NOTIFICATIONS + options.node.id}
                        channel={Channels.SMS}
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        featureName="notificationCenter"
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.EMAIL_NOTIFICATIONS: {
                    return (
                      <NotificationCenter
                        key={Features.EMAIL_NOTIFICATIONS + options.node.id}
                        channel={Channels.EMAIL}
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        featureName="emailNotifications"
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.DELIVERY_NOTIFICATIONS: {
                    return (
                      <NotificationCenter
                        key={Features.DELIVERY_NOTIFICATIONS + options.node.id}
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        featureName="deliveryNotifications"
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.BLOCK_ASSET: {
                    const { fixedPanelHeight } = options.node.options || {};

                    return (
                      <BlockAsset
                        key={Features.BLOCK_ASSET + options.node.id}
                        featureName={
                          options.node.id
                            ? `assets.${options.node.id}`
                            : 'assets'
                        }
                        featureId={options.node.id || ''}
                        fixedPanelHeight={fixedPanelHeight || undefined}
                        localeCountry={state.localeCountry}
                        localeLanguage={state.localeLanguage}
                        templateRenderingContext={state}
                      />
                    );
                  }
                  case Features.FEEDBACK: {
                    return (
                      <Feedback
                        key={Features.FEEDBACK + options.node.id}
                        featureName="feedback"
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.HELP_PILL: {
                    return (
                      // FIXME: this is a hacky solution to moving the help pill up when
                      // chat exists. there is no other way for these to talk to each other.
                      <HelpPill
                        key={Features.HELP_PILL + options.node.id}
                        featureName="helpPill"
                      />
                    );
                  }
                  case Features.MORE_INFORMATION: {
                    return (
                      <MoreInformation
                        key={Features.MORE_INFORMATION + options.node.id}
                        featureName="moreInformation"
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.PICKUP_LOCATION: {
                    return (
                      <PickupLocation
                        key={Features.PICKUP_LOCATION + options.node.id}
                        featureName="pickupLocation"
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.SECTION_DIVIDER: {
                    const { sectionDividerId } = options.node.options || {};
                    return (
                      <SectionDivider
                        key={Features.SECTION_DIVIDER + options.node.id}
                        sectionDividerId={sectionDividerId || ''}
                        featureName={
                          sectionDividerId
                            ? `sectionDividers.${sectionDividerId}`
                            : 'sectionDividers'
                        }
                      />
                    );
                  }
                  case Features.ITEMS_IN_THIS_SHIPMENT: {
                    return (
                      <ItemsInThisShipment
                        key={Features.ITEMS_IN_THIS_SHIPMENT + options.node.id}
                        featureName="itemsInThisShipment"
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.SHIPMENTS: {
                    return (
                      <Shipments
                        key={Features.SHIPMENTS + options.node.id}
                        featureName="shipments"
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.YOUTUBE: {
                    return (
                      <CustomComponent
                        key={Features.YOUTUBE + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="youtube"
                        guestComponentSettings={
                          options.node.options &&
                          options.node.options.customFeature
                        }
                      />
                    );
                  }
                  case Features.DELIVERY_INSTRUCTIONS: {
                    return (
                      <CustomComponent
                        key={Features.DELIVERY_INSTRUCTIONS + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="deliveryInstructions"
                        guestComponentSettings={
                          options.node.options &&
                          options.node.options.customFeature
                        }
                      />
                    );
                  }
                  case Features.REDIRECT_TO_HOLD: {
                    return (
                      <CustomComponent
                        key={Features.REDIRECT_TO_HOLD + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="redirectToHold"
                        guestComponentSettings={
                          options.node.options &&
                          options.node.options.customFeature
                        }
                      />
                    );
                  }
                  case Features.PICKUP_POINT_DETAILS: {
                    return (
                      <CustomComponent
                        key={Features.PICKUP_POINT_DETAILS + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="pickupPointDetails"
                        guestComponentSettings={
                          options.node.options &&
                          options.node.options.customFeature
                        }
                      />
                    );
                  }
                  case Features.SCHEDULED_DELIVERY: {
                    return (
                      <CustomComponent
                        key={Features.SCHEDULED_DELIVERY + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="scheduledDelivery"
                        guestComponentSettings={
                          options.node.options &&
                          options.node.options.customFeature
                        }
                      />
                    );
                  }
                  case Features.PRODUCT_RECOMMENDATIONS: {
                    return (
                      <CustomComponent
                        key={Features.PRODUCT_RECOMMENDATIONS + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="productRecommendations"
                        guestComponentSettings={{
                          graphqlQuery: `
                            query GetProductRecommendationsSettings(
                              $retailerMoniker: String!
                              $versionName: String!
                              $localeLanguage: Language
                              $localeCountry: Country
                              $trackingType: String
                              $trackingStatus: String
                              $cache: Boolean
                            ) {
                              retailerTranslations(
                                retailerMoniker: $retailerMoniker
                                product: "narvarjs_translations"
                                localeLanguage: $localeLanguage
                                localeCountry: $localeCountry
                                versionName: $versionName
                                status: $trackingStatus
                                type: $trackingType
                                cache: $cache
                              ) {
                                productRecommendationsIframeTitle
                                productRecommendationsCarouselTitle
                              }
                              retailerTheme(
                                product: "narvarjs"
                                retailerMoniker: $retailerMoniker
                                versionName: $versionName
                                localeLanguage: $localeLanguage
                                localeCountry: $localeCountry
                                featureName: "productRecommendations"
                                cache: $cache
                              ) {
                                # Inherited styles
                                headersMediumNone
                                headersSmallNone
                                bodytextDefaultNone
                                iconsPrimaryNone
                                panelcontainerNoneNone

                                # Arrows
                                productrecsarrowcontainerNextNone
                                productrecsarrowcontainerPrevNone
                                productrecsarrowNoneDefault
                                productrecsarrowNoneDisabled
                                productrecsarrowNoneHover

                                # Title
                                productrecscarouseltitleNoneNone

                                # Dots
                                productrecsdotcontainerNoneNone
                                productrecsdotNoneActive
                                productrecsdotNoneDefault
                                productrecsdotNoneHover

                                # Recommendation Item Container
                                productrecsreccontainerNoneNone

                                # Recommendation Container Styles
                                productrecspanelcontainerNoneNone

                                # Recommendation Item All Text
                                productrecsrectextNoneNone

                                # Recommendation Item Image
                                productrecsrecimageNoneNone

                                # Recommendation Item Name
                                productrecsrectextNameNone

                                # Recommendation Item Price
                                productrecsrecpriceNoneNone
                                productrecsrecpricePriceNone

                                # Recommendation Item Brand
                                productrecsrectextBrandNone

                                # Recommendation Item Rating
                                productrecsrecratingcontainerNoneNone
                                productrecsrecratingstarEmptyNone
                                productrecsrecratingstarFullNone

                                # Recommendation Item Sale Price
                                productrecsrecpriceSaleNone
                              }
                              retailerSettings(
                                product: "narvarjs"
                                retailerMoniker: $retailerMoniker
                                versionName: $versionName
                                localeLanguage: $localeLanguage
                                localeCountry: $localeCountry
                                cache: $cache
                              ) {
                                globalScopeCSS
                                productRecommendations {
                                  ... on ProductRecommendationsScript {
                                    type
                                    script
                                  }
                                  ... on ProductRecommendationsJson {
                                    type
                                    api {
                                      method
                                      body
                                      headers
                                      urlTemplate
                                    }
                                    itemsLookup
                                    itemsMapping {
                                      lookup
                                      type
                                      options {
                                        currency
                                        offset
                                      }
                                    }
                                    carouselSettings
                                  }
                                  ... on ProductRecommendationsMlEngine {
                                    type
                                    jsonApi: api {
                                      method
                                      body
                                      headers
                                      urlTemplate
                                    }
                                    jsonItemLookup: itemsLookup
                                    jsonItemMapping: itemsMapping {
                                      lookup
                                      type
                                      options {
                                        currency
                                        offset
                                      }
                                    }
                                    carouselSettings
                                  }
                                  ... on ProductRecommendationsCertona {
                                    type
                                    urlTemplate
                                    certonaSettings
                                    itemsLookup
                                    itemsMapping {
                                      lookup
                                      type
                                      options {
                                        currency
                                        offset
                                      }
                                    }
                                    carouselSettings
                                  }
                                }
                              }
                            }
                          `,
                          ...(options.node.options &&
                            options.node.options.customFeature),
                        }}
                      />
                    );
                  }
                  case Features.CUSTOM_COMPONENT: {
                    return (
                      <CpCustomComponent
                        key={Features.CUSTOM_COMPONENT + options.node.id}
                        componentId={options.node.id || ''}
                        featureName="customComponent"
                      />
                    );
                  }
                  case Features.DELIVERY_STATUS: {
                    return (
                      <DeliveryStatus
                        key={Features.DELIVERY_STATUS + options.node.id}
                        featureName="deliveryStatus"
                        layout={
                          (options.node.options &&
                            options.node.options.layout) ||
                          undefined
                        }
                        fixedPanelHeight={
                          (options.node.options &&
                            options.node.options.fixedPanelHeight) ||
                          undefined
                        }
                      />
                    );
                  }
                  case Features.DRIVER_DETAILS: {
                    return (
                      <DriverDetails
                        key={Features.DRIVER_DETAILS + options.node.id}
                        featureName="driverDetails"
                      />
                    );
                  }
                  case Features.DELIVERY_MAP: {
                    return (
                      <LocalDeliveryMap
                        key={Features.DELIVERY_MAP + options.node.id}
                        featureName="localDeliveryMap"
                        fixedPanelHeight={
                          options.node.options?.fixedPanelHeight ?? undefined
                        }
                      />
                    );
                  }
                  default: {
                    // NOTE: This is just for exhaustive matching.
                    ((_: never): void => {})(feature);

                    const Component =
                      customComponents && customComponents[feature];

                    if (Component) {
                      return <Component options={options} />;
                    }

                    return <></>;
                  }
                }
              };

              return (
                <ErrorBoundary errorContent={null}>
                  {renderFeature()}
                </ErrorBoundary>
              );
            }}
          />
        </main>
        <KitDynamicLayout
          userAgentDevice={userAgentDevice}
          feature={(options) => {
            if (!options || options.node.name !== Features.NARVAR_FOOTER) {
              return <></>;
            }

            return (
              <ErrorBoundary errorContent={null}>
                <NarvarFooter
                  key={Features.NARVAR_FOOTER + options.node.id}
                  featureName="narvarFooter"
                  experienceHost={state.experienceHost}
                />
              </ErrorBoundary>
            );
          }}
        />
      </ChatProvider>
    </div>
  );
};

export type DynamicTrackPageWithProvidersProps = ComponentProps<
  typeof DynamicTrackPage
> &
  ComponentProps<typeof PageProvider> & {
    noflakeUrl: string;
    serverQueries?: string;
  };

const DynamicTrackPageWithProviders = ({
  noflakeUrl,
  titanAnalyticsOptions,
  initialState,
  userAgentDevice,
  orderTrackingData,
  globalSettings,
  themeData,
  translations,
  settings,
  growthbookDisabled,
  growthbookKey,
  growthbookDecryptionKey,
  growthbookDevModeEnabled,
  serverQueries,
  ...rest
}: DynamicTrackPageWithProvidersProps) => {
  const analytics = {
    noflakeUrl,
    errorCallback,
    data: {
      tag: NoflakeTag.COMPONENT_PLATFORM,
      eventSchema: NoflakeTag.COMPONENT_PLATFORM,
      productName: initialState?.product ?? Product.narvarjs,
      pageRetailerMoniker: initialState?.retailerMoniker ?? '',
    },
    skip: initialState?.skipAnalytics,
  };

  const growthbook = growthbookDisabled
    ? undefined
    : {
        apiHost: 'https://growthbook.narvarcp.com',
        clientKey: growthbookKey,
        decryptionKey: growthbookDecryptionKey,
        subscribeToChanges: false,
        backgroundSync: false,
        // enables https://chrome.google.com/webstore/detail/growthbook-devtools/opemhndcehfgipokneipaafbglcecjia
        enableDevMode: growthbookDevModeEnabled,
      };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__APOLLO_CACHE_DATA__ = JSON.parse(serverQueries ?? '');
      window.initialState = initialState;
    }
  }, [serverQueries, initialState]);

  return (
    <PageProvider
      analytics={analytics}
      growthbook={growthbook}
      titanAnalyticsOptions={titanAnalyticsOptions}
      initialState={initialState}
      orderTrackingData={orderTrackingData}
      globalSettings={globalSettings}
      themeData={themeData}
      translations={translations}
      settings={settings}
      deviceType={userAgentDevice === 'Mobile' ? 'mobile' : 'desktop'}
    >
      <DynamicTrackPage userAgentDevice={userAgentDevice} {...rest} />
    </PageProvider>
  );
};

export default DynamicTrackPageWithProviders;
